import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import api from "../lib/api";
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save,
  X,
  ChevronUp,
  ChevronDown,
  FolderKanban,
  MessageSquare,
  Bell,
  Mail,
  Settings2,
  GripVertical,
  Info,
  CreditCard
} from "lucide-react";
import { cn } from "../lib/utils";

// Predefined colors for statuses
const STATUS_COLORS = [
  { name: "Gray", value: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20", hex: "#71717a" },
  { name: "Blue", value: "bg-blue-500/10 text-blue-400 border-blue-500/20", hex: "#3b82f6" },
  { name: "Indigo", value: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20", hex: "#6366f1" },
  { name: "Purple", value: "bg-purple-500/10 text-purple-400 border-purple-500/20", hex: "#a855f7" },
  { name: "Sky", value: "bg-sky-500/10 text-sky-400 border-sky-500/20", hex: "#0ea5e9" },
  { name: "Teal", value: "bg-teal-500/10 text-teal-400 border-teal-500/20", hex: "#14b8a6" },
  { name: "Green", value: "bg-green-500/10 text-green-400 border-green-500/20", hex: "#22c55e" },
  { name: "Emerald", value: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", hex: "#10b981" },
  { name: "Yellow", value: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", hex: "#eab308" },
  { name: "Amber", value: "bg-amber-500/10 text-amber-400 border-amber-500/20", hex: "#f59e0b" },
  { name: "Orange", value: "bg-orange-500/10 text-orange-400 border-orange-500/20", hex: "#f97316" },
  { name: "Red", value: "bg-red-500/10 text-red-400 border-red-500/20", hex: "#ef4444" },
];

// Default status configurations - matching actual app statuses
const DEFAULT_PROJECT_STATUSES = [
  { id: "proposal_sent", name: "Proposal", description: "Initial proposal stage", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", order: 0, notify_on_enter: false },
  { id: "contract_negotiation", name: "Negotiation", description: "Negotiating terms with client", color: "bg-blue-500/10 text-blue-400 border-blue-500/20", order: 1, notify_on_enter: false },
  { id: "contract_signed", name: "Signed", description: "Contract signed by client", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", order: 2, notify_on_enter: true },
  { id: "sent_to_pool", name: "In Pool", description: "Waiting for PM assignment", color: "bg-purple-500/10 text-purple-400 border-purple-500/20", order: 3, notify_on_enter: true },
  { id: "in_progress", name: "In Progress", description: "Project is actively being worked on", color: "bg-sky-500/10 text-sky-400 border-sky-500/20", order: 4, notify_on_enter: false },
  { id: "awaiting_client", name: "Awaiting Client", description: "Waiting for client response/approval", color: "bg-orange-500/10 text-orange-400 border-orange-500/20", order: 5, notify_on_enter: true },
  { id: "awaiting_payment", name: "Awaiting Payment", description: "Work done, waiting for payment", color: "bg-amber-500/10 text-amber-400 border-amber-500/20", order: 6, notify_on_enter: true },
  { id: "completed", name: "Completed", description: "Project fully completed", color: "bg-green-500/10 text-green-400 border-green-500/20", order: 7, notify_on_enter: true },
];

const DEFAULT_PAYMENT_STATUSES = [
  { id: "pending", name: "Pending", description: "Payment not yet processed", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", order: 0, notify_on_enter: false },
  { id: "approved", name: "Approved", description: "Payment approved, awaiting transfer", color: "bg-blue-500/10 text-blue-400 border-blue-500/20", order: 1, notify_on_enter: true },
  { id: "paid", name: "Paid", description: "Payment completed", color: "bg-green-500/10 text-green-400 border-green-500/20", order: 2, notify_on_enter: true },
];

const DEFAULT_REQUEST_STATUSES = [
  { id: "pending", name: "Pending", description: "Awaiting client response", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", order: 0, notify_on_enter: true },
  { id: "responded", name: "Responded", description: "Client has responded", color: "bg-green-500/10 text-green-400 border-green-500/20", order: 1, notify_on_enter: true },
];

const StatusSettingsPage = () => {
  const [activeTab, setActiveTab] = useState("projects");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Status configurations
  const [projectStatuses, setProjectStatuses] = useState(DEFAULT_PROJECT_STATUSES);
  const [paymentStatuses, setPaymentStatuses] = useState(DEFAULT_PAYMENT_STATUSES);
  const [requestStatuses, setRequestStatuses] = useState(DEFAULT_REQUEST_STATUSES);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    description: "",
    color: "",
    notify_on_enter: false,
    notify_emails: ""
  });
  
  // Add new status dialog
  const [addDialog, setAddDialog] = useState(null);
  const [addForm, setAddForm] = useState({
    id: "",
    name: "",
    description: "",
    color: STATUS_COLORS[0].value,
    notify_on_enter: false,
    notify_emails: ""
  });

  const fetchStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/settings/statuses");
      const data = response.data;
      
      if (data.project_statuses?.length > 0) {
        setProjectStatuses(data.project_statuses);
      }
      if (data.payment_statuses?.length > 0) {
        setPaymentStatuses(data.payment_statuses);
      }
      if (data.request_statuses?.length > 0) {
        setRequestStatuses(data.request_statuses);
      }
    } catch (error) {
      // If no saved config, use defaults - that's OK
      console.log("Using default status configurations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const saveStatuses = async () => {
    try {
      setSaving(true);
      await api.put("/settings/statuses", {
        project_statuses: projectStatuses,
        payment_statuses: paymentStatuses,
        request_statuses: requestStatuses
      });
      toast.success("Status settings saved successfully");
    } catch (error) {
      console.error("Error saving statuses:", error);
      toast.error("Failed to save status settings");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (type, status) => {
    setEditDialog({ type, status });
    setEditForm({
      id: status.id,
      name: status.name,
      description: status.description || "",
      color: status.color,
      notify_on_enter: status.notify_on_enter || false,
      notify_emails: status.notify_emails || ""
    });
  };
  
  const openAddDialog = (type) => {
    setAddDialog({ type });
    setAddForm({
      id: "",
      name: "",
      description: "",
      color: STATUS_COLORS[0].value,
      notify_on_enter: false,
      notify_emails: ""
    });
  };
  
  const closeAddDialog = () => {
    setAddDialog(null);
    setAddForm({
      id: "",
      name: "",
      description: "",
      color: STATUS_COLORS[0].value,
      notify_on_enter: false,
      notify_emails: ""
    });
  };
  
  const addNewStatus = () => {
    if (!addDialog || !addForm.id || !addForm.name) {
      toast.error("Please fill in ID and Name");
      return;
    }
    
    const { type } = addDialog;
    const newStatus = {
      id: addForm.id.toLowerCase().replace(/\s+/g, '_'),
      name: addForm.name,
      description: addForm.description,
      color: addForm.color,
      notify_on_enter: addForm.notify_on_enter,
      notify_emails: addForm.notify_emails,
      order: 0
    };
    
    // Check for duplicate ID
    const getStatuses = type === "projects" ? projectStatuses : 
      type === "payments" ? paymentStatuses : requestStatuses;
    
    if (getStatuses.some(s => s.id === newStatus.id)) {
      toast.error("Status with this ID already exists");
      return;
    }
    
    // Add to end and update order
    if (type === "projects") {
      newStatus.order = projectStatuses.length;
      setProjectStatuses(prev => [...prev, newStatus]);
    } else if (type === "payments") {
      newStatus.order = paymentStatuses.length;
      setPaymentStatuses(prev => [...prev, newStatus]);
    } else if (type === "requests") {
      newStatus.order = requestStatuses.length;
      setRequestStatuses(prev => [...prev, newStatus]);
    }
    
    closeAddDialog();
    toast.success("Status added - don't forget to save!");
  };
  
  const deleteStatus = (type, statusId) => {
    if (type === "projects") {
      setProjectStatuses(prev => prev.filter(s => s.id !== statusId).map((s, i) => ({ ...s, order: i })));
    } else if (type === "payments") {
      setPaymentStatuses(prev => prev.filter(s => s.id !== statusId).map((s, i) => ({ ...s, order: i })));
    } else if (type === "requests") {
      setRequestStatuses(prev => prev.filter(s => s.id !== statusId).map((s, i) => ({ ...s, order: i })));
    }
    toast.success("Status removed - don't forget to save!");
  };

  const closeEditDialog = () => {
    setEditDialog(null);
    setEditForm({
      id: "",
      name: "",
      description: "",
      color: "",
      notify_on_enter: false,
      notify_emails: ""
    });
  };

  // Drag & Drop functionality
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const handleDragStart = (e, type, index) => {
    setDraggedItem({ type, index });
    e.dataTransfer.effectAllowed = "move";
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, type, index) => {
    e.preventDefault();
    if (draggedItem && draggedItem.type === type) {
      setDragOverItem({ type, index });
    }
  };

  const handleDrop = (e, type, dropIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== type) return;
    
    const dragIndex = draggedItem.index;
    if (dragIndex === dropIndex) return;
    
    const reorder = (items) => {
      const newItems = [...items];
      const [removed] = newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, removed);
      return newItems.map((item, i) => ({ ...item, order: i }));
    };
    
    if (type === "projects") {
      setProjectStatuses(reorder);
    } else if (type === "payments") {
      setPaymentStatuses(reorder);
    } else if (type === "requests") {
      setRequestStatuses(reorder);
    }
    
    setDraggedItem(null);
    setDragOverItem(null);
    toast.success("Order updated - don't forget to save!");
  };

  const saveEditedStatus = () => {
    if (!editDialog) return;
    
    const { type } = editDialog;
    const updatedStatus = {
      ...editDialog.status,
      name: editForm.name,
      description: editForm.description,
      color: editForm.color,
      notify_on_enter: editForm.notify_on_enter,
      notify_emails: editForm.notify_emails
    };
    
    if (type === "projects") {
      setProjectStatuses(prev => prev.map(s => s.id === updatedStatus.id ? updatedStatus : s));
    } else if (type === "payments") {
      setPaymentStatuses(prev => prev.map(s => s.id === updatedStatus.id ? updatedStatus : s));
    } else if (type === "requests") {
      setRequestStatuses(prev => prev.map(s => s.id === updatedStatus.id ? updatedStatus : s));
    }
    
    closeEditDialog();
    toast.success("Status updated - don't forget to save!");
  };

  const moveStatus = (type, index, direction) => {
    const setStatuses = type === "projects" ? setProjectStatuses : 
      type === "payments" ? setPaymentStatuses : setRequestStatuses;
    
    setStatuses(prev => {
      const newStatuses = [...prev];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= newStatuses.length) return prev;
      
      [newStatuses[index], newStatuses[newIndex]] = [newStatuses[newIndex], newStatuses[index]];
      // Update order values
      return newStatuses.map((s, i) => ({ ...s, order: i }));
    });
  };

  const renderStatusTable = (type, statuses) => (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800 hover:bg-transparent">
            <TableHead className="w-8"></TableHead>
            <TableHead className="w-12 text-zinc-400">#</TableHead>
            <TableHead className="text-zinc-400">Status ID</TableHead>
            <TableHead className="text-zinc-400">Display Name</TableHead>
            <TableHead className="text-zinc-400">Description</TableHead>
            <TableHead className="text-zinc-400 text-center">Notify</TableHead>
            <TableHead className="text-zinc-400 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {statuses.map((status, index) => (
            <TableRow 
              key={status.id} 
              className={cn(
                "border-zinc-800 transition-colors",
                dragOverItem?.type === type && dragOverItem?.index === index && "bg-orange-500/10"
              )}
              draggable
              onDragStart={(e) => handleDragStart(e, type, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, type, index)}
              onDrop={(e) => handleDrop(e, type, index)}
            >
              <TableCell className="cursor-grab active:cursor-grabbing">
                <GripVertical className="h-4 w-4 text-zinc-600 hover:text-zinc-400" />
              </TableCell>
              <TableCell className="font-mono text-zinc-500 text-sm">
                {index + 1}
              </TableCell>
              <TableCell className="font-mono text-zinc-500 text-xs">
                {status.id}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={cn("text-xs", status.color)}>
                  {status.name}
                </Badge>
              </TableCell>
              <TableCell className="text-zinc-400 text-sm max-w-xs truncate">
                {status.description || "—"}
              </TableCell>
              <TableCell className="text-center">
                {status.notify_on_enter ? (
                  <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
                    <Bell className="h-3 w-3 mr-1" />
                    On
                  </Badge>
                ) : (
                  <span className="text-zinc-600 text-xs">Off</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveStatus(type, index, "up")}
                    disabled={index === 0}
                    className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-50"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveStatus(type, index, "down")}
                    disabled={index === statuses.length - 1}
                    className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-50"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(type, status)}
                    className="h-7 w-7 p-0 text-zinc-500 hover:text-orange-400"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteStatus(type, status.id)}
                    className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="status-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-orange-500" />
            Project & Payment Statuses
          </h1>
          <p className="text-sm text-zinc-500">
            Statuses of projects, payments and client requests. Tasks move through their timeline stages instead.
          </p>
        </div>
        <Button 
          onClick={saveStatuses} 
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save All Changes
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-300">
            <p className="font-medium text-blue-400 mb-1">How notifications work</p>
            <p className="text-zinc-400">
              When Notifications is enabled for a status, an email will be sent to relevant parties 
              when an item enters that status. Tasks have no status — they move through
              their timeline stages (Settings → Timelines). For projects, notifications go to the PM and Sales Manager.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="projects" className="data-[state=active]:bg-zinc-800">
            <FolderKanban className="h-4 w-4 mr-2" />
            Projects ({projectStatuses.length})
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-zinc-800">
            <CreditCard className="h-4 w-4 mr-2" />
            Payments ({paymentStatuses.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-zinc-800">
            <MessageSquare className="h-4 w-4 mr-2" />
            Requests ({requestStatuses.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                Project workflow: Proposal → Negotiation → Signed → In Pool → In Progress → Awaiting Client → Awaiting Payment → Completed
              </p>
              <Button size="sm" onClick={() => openAddDialog("projects")} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-1" />
                Add Status
              </Button>
            </div>
            {renderStatusTable("projects", projectStatuses)}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                Payment workflow: Pending → Approved → Paid
              </p>
              <Button size="sm" onClick={() => openAddDialog("payments")} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-1" />
                Add Status
              </Button>
            </div>
            {renderStatusTable("payments", paymentStatuses)}
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                Client request workflow: Pending → Responded
              </p>
              <Button size="sm" onClick={() => openAddDialog("requests")} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-1" />
                Add Status
              </Button>
            </div>
            {renderStatusTable("requests", requestStatuses)}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => closeEditDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-orange-500" />
              Edit Status
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400">Status ID</Label>
              <Input
                value={editForm.id}
                disabled
                className="bg-zinc-800 border-zinc-700 text-zinc-500 font-mono"
              />
              <p className="text-xs text-zinc-600">System identifier (cannot be changed)</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Display Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                placeholder="e.g., In Progress"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 resize-none"
                placeholder="Brief description of what this status means..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Color</Label>
              <div className="grid grid-cols-6 gap-2">
                {STATUS_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => setEditForm({ ...editForm, color: color.value })}
                    className={cn(
                      "w-8 h-8 rounded-lg border-2 transition-all",
                      editForm.color === color.value 
                        ? "border-white scale-110" 
                        : "border-transparent hover:scale-105"
                    )}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
              <p className="text-xs text-zinc-600">Preview:</p>
              <Badge variant="outline" className={cn("text-xs", editForm.color)}>
                {editForm.name || "Status"}
              </Badge>
            </div>
            
            <div className="space-y-3 pt-2 border-t border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-zinc-300">Email Notifications</Label>
                  <p className="text-xs text-zinc-500">Send email when item enters this status</p>
                </div>
                <Switch
                  checked={editForm.notify_on_enter}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, notify_on_enter: checked })}
                />
              </div>
              
              {editForm.notify_on_enter && (
                <div className="space-y-2">
                  <Label className="text-zinc-400">Additional Emails (optional)</Label>
                  <Input
                    value={editForm.notify_emails}
                    onChange={(e) => setEditForm({ ...editForm, notify_emails: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="text-xs text-zinc-500">
                    Comma-separated list. Default recipients (PM, artist) always included.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={saveEditedStatus} className="bg-orange-500 hover:bg-orange-600">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Status Dialog */}
      <Dialog open={!!addDialog} onOpenChange={() => closeAddDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Plus className="h-5 w-5 text-orange-500" />
              Add New Status
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400">Status ID *</Label>
              <Input
                value={addForm.id}
                onChange={(e) => setAddForm({ ...addForm, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono"
                placeholder="e.g., on_hold"
              />
              <p className="text-xs text-zinc-500">Unique identifier (lowercase, underscores)</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Display Name *</Label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                placeholder="e.g., On Hold"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 resize-none"
                placeholder="Brief description..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Color</Label>
              <div className="grid grid-cols-6 gap-2">
                {STATUS_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => setAddForm({ ...addForm, color: color.value })}
                    className={cn(
                      "w-8 h-8 rounded-lg border-2 transition-all",
                      addForm.color === color.value 
                        ? "border-white scale-110" 
                        : "border-transparent hover:scale-105"
                    )}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
              <p className="text-xs text-zinc-600">Preview:</p>
              <Badge variant="outline" className={cn("text-xs", addForm.color)}>
                {addForm.name || "New Status"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
              <div>
                <Label className="text-zinc-300">Email Notifications</Label>
                <p className="text-xs text-zinc-500">Send email when item enters this status</p>
              </div>
              <Switch
                checked={addForm.notify_on_enter}
                onCheckedChange={(checked) => setAddForm({ ...addForm, notify_on_enter: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeAddDialog} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={addNewStatus} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-1" />
              Add Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatusSettingsPage;
